import { useEffect, useState, useRef, Fragment } from 'react'

import * as React from 'react'
import { BlankProfilePicture, Resume } from '@rio/types'

import { AsyncAutosuggest, createSuggestionsApiFetcher } from 'builder/components/AsyncAutosuggest'
import EditorField from 'builder/components/EditorField'
import EditorRow from 'builder/components/EditorRow'
import Foldable from 'builder/components/Foldable'
import PhotoUploadField from 'builder/components/PhotoUploadField'
import SectionTitle from 'builder/components/SectionTitle'
import {
  SectionBodyFoldableContainer,
  SectionHeader,
  SectionHeaderFoldableContainer,
  SectionHint,
} from 'builder/components/Section'
import { TunerSectionTooltips } from 'builder/components/Tuner'
import { TextField } from 'builder/components/TextField'

import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import FocusManager from 'builder/services/FocusManager'

import {
  selectors as editorSelectors,
  SectionNames,
  AvailableAISuggestions,
} from 'builder/modules/resumeEditor'
import { AISuggestionsToggler } from 'builder/components//AiFeatures/Suggestions'
import { GuestResumeContext } from 'builder/context/GuestResumeContext'
import { FetchStatuses } from 'builder/modules/constants'
import {
  Container,
  ShowButton,
  ShowButtonText,
  ShowButtonIcon,
  AdditionalFields,
} from '../PersonalDetailsSection/styles'
import Checkbox from '../Checkbox'
import {
  StyledEditorField,
  StyledSpinner,
  SpinnerContainer,
  SuccessIcon,
  ReceiveAdsContainer,
} from './styles'

type Props = {
  backendValidationMessage: string
  emailValidationStatus: FetchStatuses
  isConsentChecked: boolean
  setIsConsentChecked: (value: boolean) => void
}

const avatar = {
  blank: true,
  thumbUrl: null,
  mediumUrl: null,
  originalUrl: null,
  transform: null,
} as BlankProfilePicture

export const PersonalDetailsSectionGuest = (props: Props) => {
  const { emailValidationStatus, backendValidationMessage, isConsentChecked, setIsConsentChecked } =
    props
  const { i18n } = useI18n()
  const templates = useTypedSelector(editorSelectors.templates)

  const container = useRef<HTMLDivElement | null>(null)
  const emailFieldRef = useRef<HTMLDivElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const [areAdditionalFieldsVisible, setAdditionalFieldsVisibility] = useState(false)

  const {
    resume,
    updateResumeFields,
    toggleEmailRequiredError,
    showEmailRequiredError,
    shouldFocusEmailField,
  } = React.useContext(GuestResumeContext)
  // SUGGESTIONS NOT WORKING ON LOCALHOST
  const {
    email,
    locale = 'en',
    template,
    firstName,
    lastName,
    phoneNumber,
    city,
    address,
    birthDate,
    birthPlace,
    postalCode,
    position,
    countryName,
    drivingLicense,
    nationality,
  } = resume

  const currentTemplate = templates.find(t => t.id === template)
  const supportsPhoto = currentTemplate ? currentTemplate.supportsPhoto : false

  const emailErrorMessage = showEmailRequiredError
    ? i18n.t('builder.sign_up.field_is_required')
    : backendValidationMessage || ''

  const onEmailChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    updateResumeFields({ email: event.target.value })
  }

  // Connect to FocusManager
  useEffect(() => {
    const focus = (name: string) => {
      const targetField = container.current?.querySelector<HTMLInputElement>(`[name=${name}]`)
      if (targetField) targetField.focus()
    }

    const focusableFieldNames: Array<keyof Resume> = ['position', 'email', 'phoneNumber']
    focusableFieldNames.forEach(name => FocusManager.subscribe(name, () => focus(name)))
    return () => focusableFieldNames.forEach(name => FocusManager.unsubscribe(name))
  }, [])

  useEffect(() => {
    if (shouldFocusEmailField) {
      emailInputRef.current?.focus()
      emailFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [shouldFocusEmailField])

  return (
    <Fragment>
      <Container ref={container}>
        {/* Section header */}
        <SectionHeader isOpened={true} isStepByStepFlow={true}>
          <SectionTitle
            title={i18n.t('builder.resume_editor.personal_details')}
            isOpened={true}
            isFoldable={false}
          />
          <SectionHeaderFoldableContainer isOpened={true}>
            <SectionHint>
              {i18n.t('builder.resume_editor.personal_details_section.label')}
            </SectionHint>
          </SectionHeaderFoldableContainer>
        </SectionHeader>

        <SectionBodyFoldableContainer isOpened={true} isStepByStepFlow={true}>
          <EditorRow>
            {/* Job title */}
            <EditorField>
              <AsyncAutosuggest
                name="position"
                fetchItems={createSuggestionsApiFetcher('job-title', { limit: 4, locale })}
                value={position || ''}
                onChange={event => updateResumeFields({ position: event.target.value })}
              >
                {({ key, ...inputProps }) => (
                  <TextField
                    {...inputProps}
                    key={key}
                    placeholder={i18n.t('builder.resume_editor.job_title_placeholder')}
                    label={i18n.t('builder.resume_editor.job_title')}
                    hint={i18n.t('builder.resume_editor.job_title_hint')}
                    badges={<AISuggestionsToggler type={AvailableAISuggestions.jobTitle} />}
                  />
                )}
              </AsyncAutosuggest>
            </EditorField>

            {/* Avatar */}
            <EditorField>
              <PhotoUploadField
                imageUrl={avatar.blank ? '' : avatar.thumbUrl}
                isUploading={false}
                isLocked={!supportsPhoto}
                onUpload={() => {}}
                onDelete={() => {}}
                onClick={toggleEmailRequiredError}
              />
            </EditorField>
          </EditorRow>

          <EditorRow>
            {/* First name */}
            <EditorField>
              <TextField
                label={i18n.t('builder.resume_editor.first_name')}
                value={firstName || ''}
                name="firstName"
                onChange={event => updateResumeFields({ firstName: event.target.value })}
              />
            </EditorField>

            {/* Last name */}
            <EditorField>
              <TextField
                label={i18n.t('builder.resume_editor.last_name')}
                value={lastName || ''}
                name="lastName"
                onChange={event => updateResumeFields({ lastName: event.target.value })}
              />
            </EditorField>
          </EditorRow>

          <EditorRow>
            {/* Email */}
            <StyledEditorField elementRef={emailFieldRef}>
              <TextField
                ref={emailInputRef}
                label={`${i18n.t('builder.resume_editor.email')}*`}
                type="email"
                value={email || ''}
                name="email"
                onChange={onEmailChange}
                error={emailErrorMessage}
                inputIcon={
                  emailValidationStatus === FetchStatuses.loading ? (
                    <SpinnerContainer>
                      <StyledSpinner />
                    </SpinnerContainer>
                  ) : emailValidationStatus === FetchStatuses.loaded ? (
                    <SuccessIcon />
                  ) : null
                }
                inputIconPosition="right"
              />
              <ReceiveAdsContainer>
                {emailValidationStatus !== FetchStatuses.notAsked && !emailErrorMessage && (
                  <Checkbox
                    label={i18n.t('builder.sign_up.consent_checkbox_label_guest_builder')}
                    checked={isConsentChecked}
                    onChange={event => setIsConsentChecked(event.target.checked)}
                    name="confirm_email"
                    align="top"
                  />
                )}
              </ReceiveAdsContainer>
            </StyledEditorField>

            {/* Phone */}
            <EditorField>
              <TextField
                label={i18n.t('builder.resume_editor.phone')}
                type="phone"
                value={phoneNumber || ''}
                name="phoneNumber"
                onChange={event => updateResumeFields({ phoneNumber: event.target.value })}
              />
            </EditorField>
          </EditorRow>

          <EditorRow>
            {/* Address */}
            <EditorField fullWidth>
              <TextField
                label={i18n.t('builder.resume_editor.address')}
                value={address || ''}
                name="address"
                onChange={event => updateResumeFields({ address: event.target.value })}
              />
            </EditorField>
          </EditorRow>

          <EditorRow>
            {/* City */}
            <EditorField>
              <AsyncAutosuggest
                highlightedQuery
                fetchItems={createSuggestionsApiFetcher('city', {
                  locale,
                  country: countryName || '',
                })}
                name="city"
                value={city || ''}
                onChange={event => updateResumeFields({ city: event.target.value })}
              >
                {({ key, ...inputProps }) => (
                  <TextField
                    {...inputProps}
                    key={key}
                    label={i18n.t('builder.resume_editor.city')}
                  />
                )}
              </AsyncAutosuggest>
            </EditorField>

            {/* Country */}
            <EditorField>
              <AsyncAutosuggest
                highlightedQuery
                name="countryName"
                fetchItems={createSuggestionsApiFetcher('country', { locale })}
                value={countryName || ''}
                onChange={event => updateResumeFields({ countryName: event.target.value })}
              >
                {({ key, ...inputProps }) => (
                  <TextField
                    {...inputProps}
                    key={key}
                    label={i18n.t('builder.resume_editor.country')}
                  />
                )}
              </AsyncAutosuggest>
            </EditorField>
          </EditorRow>

          {/* Additional fields */}
          <Foldable isOpen={areAdditionalFieldsVisible}>
            <AdditionalFields>
              <EditorRow>
                {/* ZIP Code */}
                <EditorField>
                  <TextField
                    label={i18n.t('builder.resume_editor.postal_code')}
                    value={postalCode || ''}
                    name="postalCode"
                    onChange={event => updateResumeFields({ postalCode: event.target.value })}
                  />
                </EditorField>

                {/* Driving License */}
                <EditorField>
                  <TextField
                    label={i18n.t('builder.resume_editor.driving_license')}
                    hint={i18n.t('builder.resume_editor.driving_license_hint')}
                    value={drivingLicense || ''}
                    name="drivingLicense"
                    onChange={event => updateResumeFields({ drivingLicense: event.target.value })}
                  />
                </EditorField>
              </EditorRow>

              <EditorRow>
                {/* Birth date */}
                <EditorField>
                  <TextField
                    label={i18n.t('builder.resume_editor.birth_date')}
                    hint={i18n.t('builder.resume_editor.birth_date_hint')}
                    value={birthDate || ''}
                    name="birthDate"
                    onChange={event => updateResumeFields({ birthDate: event.target.value })}
                  />
                </EditorField>

                {/* Birth place */}
                <EditorField>
                  <TextField
                    label={i18n.t('builder.resume_editor.birth_place')}
                    value={birthPlace || ''}
                    name="birthPlace"
                    onChange={event => updateResumeFields({ birthPlace: event.target.value })}
                  />
                </EditorField>
              </EditorRow>

              <EditorRow>
                {/* Nationality */}
                <EditorField>
                  <AsyncAutosuggest
                    name="nationality"
                    fetchItems={createSuggestionsApiFetcher('nationality', { locale })}
                    value={nationality || ''}
                    onChange={event => updateResumeFields({ nationality: event.target.value })}
                  >
                    {({ key, ...inputProps }) => (
                      <TextField
                        {...inputProps}
                        key={key}
                        label={i18n.t('builder.resume_editor.nationality')}
                        hint={i18n.t('builder.resume_editor.nationality_hint')}
                      />
                    )}
                  </AsyncAutosuggest>
                </EditorField>
              </EditorRow>
            </AdditionalFields>
          </Foldable>

          {/* Additional fields expand button */}
          <ShowButton onClick={() => setAdditionalFieldsVisibility(value => !value)}>
            <ShowButtonText>
              {areAdditionalFieldsVisible
                ? i18n.t('builder.resume_editor.hide_details')
                : i18n.t('builder.resume_editor.show_details')}
            </ShowButtonText>
            <ShowButtonIcon data-open={areAdditionalFieldsVisible} />
          </ShowButton>

          <TunerSectionTooltips sectionId={SectionNames.details} />
        </SectionBodyFoldableContainer>
      </Container>
    </Fragment>
  )
}
